import type React from 'react';
import { useEffect } from 'react';

import { Box } from '@zillow/constellation-10';

import type { Field, Section } from '../../../models/index';
import { SECURITY_DEPOSIT } from '../../../constants/index';
import { FEE_PAYMENT_TIME, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import FeePaymentTime from '../../../../shared/components/Widgets/FeePaymentTime';
import SecurityDepositYesAndNoExpansion from './SecurityDeposit/SecurityDepositYesAndNoExpansion';
import SecurityDepositCurrencyInput from './SecurityDeposit/SecurityDepositCurrencyInput';
import SecurityDepositCleaningCostYesAndNo from './SecurityDeposit/SecurityDepositCleaningCostYesAndNo';
import SecurityDepositInterestYesAndNo from './SecurityDeposit/SecurityDepositInterestYesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function SecurityDeposit_v4({ section, setSectionValues }: Props) {
  useEffect(() => {
    section.setCustomFieldValidator(SECURITY_DEPOSIT.FIELD_NAME.AMOUNT, isAmountValid);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  function isAmountValid(amount: string, field?: Field): boolean {
    if (Number(amount.replace(/,/g, '')) > SECURITY_DEPOSIT.BOUNDS.MAX_AMOUNT) {
      (field ?? section.getField(SECURITY_DEPOSIT.FIELD_NAME.AMOUNT)).setErrorMessage(
        MAX_AMOUNT_ERROR_MESSAGE(SECURITY_DEPOSIT.BOUNDS.MAX_AMOUNT),
      );
      return false;
    }
    return true;
  }

  function disableSecurityDeposit() {
    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: false });
  }

  function enableSecurityDeposit() {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: true,
      [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: '',
    });
  }

  function onAmountChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: value });
  }

  function onPayDueClick(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING]: value === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE]: value === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
    });
  }

  function onCleaningCostYesClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST]: true,
    });
  }

  function onCleaningCostNoClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST]: false,
    });
  }

  function onInterestYesClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.INTEREST]: true,
    });
  }

  function onInterestNoClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.INTEREST]: false,
    });
  }

  const cleaningCostFieldValue = section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST);
  const interestFieldValue = section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.INTEREST);

  return (
    <SecurityDepositYesAndNoExpansion
      yesHandler={enableSecurityDeposit}
      noHandler={disableSecurityDeposit}
      yesActive={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED)}
      disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED)}
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'layout.tight', marginTop: 'layout.tight' }}>
        <SecurityDepositCurrencyInput
          dataTest="security-deposit-amount"
          onBlur={onAmountChange}
          onChange={onAmountChange}
          section={section}
        />
        <FeePaymentTime
          label="When is the security deposit due?"
          section={section}
          onChange={onPayDueClick}
          firstChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING)}
          secondChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE)}
        />
        <SecurityDepositCleaningCostYesAndNo
          yesHandler={onCleaningCostYesClick}
          noHandler={onCleaningCostNoClick}
          yesActive={cleaningCostFieldValue}
          disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST)}
        />
        <SecurityDepositInterestYesAndNo
          yesHandler={onInterestYesClick}
          noHandler={onInterestNoClick}
          yesActive={interestFieldValue}
          disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.INTEREST)}
        />
      </Box>
    </SecurityDepositYesAndNoExpansion>
  );
}

SecurityDeposit_v4.description =
  'Asks for security deposit, when the deposit is due, yes/no for cleaning cost, and yes/no for interest.';
